// @flow

import * as React from 'react';
import { pageStyles } from 'src/navigation/defaultPageStyles';
import { Pressable, View } from 'react-native';
import Text from 'src/components/CustomText/CustomText';
import { FormattedMessage } from 'react-intl';
import styles from 'src/navigation/Profile/ProfileStyles';
import { isEmpty } from 'lodash';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import AddedChannelItem from 'src/navigation/Profile/AddedChannelItem';
import { APIContext } from 'speed-js-react';

type Props = {
    width: any,
    currentProfile: Object | null,
    addedChannels: Array<Object>,
    channelsErrorRef: Object,
    setAddedChannels: Function,
    setRemainingChannels: Function,
    remainingChannels: Array<Object>,
    errors: any,
    profileUuid: string | undefined,
    accountUUUD: string,
    channels: Array<Object>,
    setResetOrder: Function,
    isDefault: string,
}

export const onDragEnd = (result, array, setArray) => {
    const { source, destination } = result;
    // the item has been dragged
    if (destination) {
        // the item has been dragged to a different position from its original one
        if (destination.index !== source.index) {
            // copy the state array
            const channelArray = [...array];
            // get the item that has been moved
            const movedChannel = channelArray[source.index];
            // remove the moved item from the array
            channelArray.splice(source.index, 1);
            // insert the moved item to the destination place
            channelArray.splice(destination.index, 0, movedChannel);
            // update the state with the new order
            setArray(channelArray);
        }
    }
};

const AddedChannelsTable = (props: Props) => {
    const {
        width, currentProfile, addedChannels, channelsErrorRef, setAddedChannels,
        setRemainingChannels, remainingChannels, errors, profileUuid, accountUUUD, channels, setResetOrder,
        isDefault,
    } = props;

    const { apiClient } = React.useContext(APIContext);

    const removeAllChannels = React.useCallback(() => {
        setRemainingChannels([...remainingChannels, ...addedChannels].sort(
            (a, b) => (a.number - b.number),
        ));
        setAddedChannels([]);
    }, [addedChannels, remainingChannels, setAddedChannels, setRemainingChannels]);

    const resetOrder = React.useCallback(() => {
        if (profileUuid && currentProfile) {
            setResetOrder(true);
            apiClient.resources.profiles.channels.list(currentProfile.parent, {
                limit: 300,
                account_uuid: accountUUUD,
            })
                .then((profileChannels) => {
                    const channelUuids = profileChannels.results.map((pc) => pc.channel.uuid);
                    const channelsExcludedFromProfile = channels.filter(
                        (el) => !channelUuids.includes(el.uuid),
                    );
                    setRemainingChannels(channelsExcludedFromProfile);
                    const channelsInProfile = profileChannels.results.sort(
                        (a, b) => a.sequence - b.sequence,
                    ).map((el) => el.channel);
                    setAddedChannels(channelsInProfile);
                });
        }
    }, [
        accountUUUD, apiClient.resources.profiles.channels, channels, currentProfile, profileUuid,
        setAddedChannels, setRemainingChannels, setResetOrder,
    ]);

    return (
        <View style={pageStyles.added}>
            <View style={width > 600 ? pageStyles.channels_label : pageStyles.channels_label_phone}>
                <Text style={width > 600 ? pageStyles.available_channels_label
                    : pageStyles.available_channels_label_phone}
                >
                    <FormattedMessage
                        id="added-channels-in-profile"
                        defaultMessage="Added Channels In Profile"
                    />
                </Text>
                <View style={styles.device_buttons_wrapper}>
                    {(currentProfile && !currentProfile.parent) && (
                        <Pressable
                            onPress={removeAllChannels}
                            style={pageStyles.centered}
                        >
                            <Text style={[width > 600 ? pageStyles.light_blue
                                : [pageStyles.light_blue, pageStyles.centered],
                            width < 600 && pageStyles.up_button]}
                            >
                                <FormattedMessage
                                    id="remove-all-channels"
                                    defaultMessage="Remove All Channels"
                                />
                            </Text>
                        </Pressable>
                    )}
                    {(currentProfile && currentProfile.parent) && (
                        <Pressable
                            onPress={resetOrder}
                            style={pageStyles.centered}
                        >
                            <Text style={[width > 600 ? pageStyles.light_blue
                                : [pageStyles.light_blue, pageStyles.centered],
                            width < 600 && pageStyles.up_button]}
                            >
                                <FormattedMessage
                                    id="reset-order"
                                    defaultMessage="Reset order"
                                />
                            </Text>
                        </Pressable>
                    )}
                </View>
            </View>
            <View
                style={pageStyles.channel_label_container}
            >
                <View
                    style={width > 600
                        ? pageStyles.channel_label_first_column
                        : pageStyles.channel_label_first_column_phone}
                />
                <Text style={pageStyles.channel_label_second_column}>
                    <FormattedMessage
                        id="channel"
                        defaultMessage="Channel"
                    />
                </Text>
                {width > 600 && (
                    <>
                        <Text style={width > 600 ? pageStyles.channel_label_third_column
                            : pageStyles.channel_label_container}
                        >
                            <FormattedMessage
                                id="category"
                                defaultMessage="Category"
                            />
                        </Text>
                        <Text style={width > 600 ? pageStyles.channel_label_fourth_column
                            : styles.added_channels_labels_phone}
                        >
                            <FormattedMessage
                                id="resolution"
                                defaultMessage="Resolution"
                            />
                        </Text>
                    </>
                )}
                <View style={pageStyles.channel_label_fifth_column} />
            </View>
            {isEmpty(addedChannels) && (
                <View style={pageStyles.empty_row_styles} />
            )}
            <View ref={channelsErrorRef}>
                {errors?.channels && (
                    <Text style={pageStyles.primary_error}>
                        {errors.channels}
                    </Text>
                )}
            </View>
            <DragDropContext onDragEnd={(result) => onDragEnd(result, addedChannels, setAddedChannels)}>
                <Droppable droppableId="channelsList">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {addedChannels.map((item, index) => (
                                <AddedChannelItem
                                    isDefault={isDefault}
                                    key={item.uuid}
                                    item={item}
                                    index={index}
                                    addedChannels={addedChannels}
                                    setAddedChannels={setAddedChannels}
                                    setRemainingChannels={setRemainingChannels}
                                    channels={remainingChannels}
                                />
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </View>
    );
};

export default AddedChannelsTable;
